import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { darkTheme, keyframes, styled, vh } from "~/ui/style/stitches.config";
import { getGuidebookByCategory, GuidebookMeaning } from "~/api/guidebookApi";
import { imageUrl } from "~/utils/imageurl";
import { GuidebookCardStack } from "./GuidebookCardStack";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { GuidebookMeaningContent } from "./GuidebookMeaning";
import { isIOSNativeApp } from "~/utils/deviceDetection";
import { trackEvent } from "~/api/analyticsApi";

// Check if we're on mobile for scroll-triggered animations
const isMobileDevice = () => window.matchMedia("(max-width: 640px)").matches;

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const fadeInWithPointerDelay = keyframes({
  "0%": { opacity: 0, pointerEvents: "none" },
  "50%": { opacity: 0.5, pointerEvents: "none" },
  "100%": { opacity: 1, pointerEvents: "auto" },
});

// Convert spelled-out numbers to digits for compact display
const shortenCardName = (name: string): string => {
  const numberMap: Record<string, string> = {
    "One": "1", "Two": "2", "Three": "3", "Four": "4", "Five": "5",
    "Six": "6", "Seven": "7", "Eight": "8", "Nine": "9", "Ten": "10",
  };
  return name.replace(/^(One|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten)\b/i, (match) =>
    numberMap[match.charAt(0).toUpperCase() + match.slice(1).toLowerCase()] || match
  );
};

// Add Roman numerals for Major Arcana cards
const MAJOR_ARCANA_NUMERALS: Record<string, string> = {
  "The Fool": "0", "The Magician": "I", "The High Priestess": "II", "The Empress": "III",
  "The Emperor": "IV", "The Hierophant": "V", "The Lovers": "VI", "The Chariot": "VII",
  "Strength": "VIII", "The Hermit": "IX", "Wheel of Fortune": "X", "Justice": "XI",
  "The Hanged Man": "XII", "Death": "XIII", "Temperance": "XIV", "The Devil": "XV",
  "The Tower": "XVI", "The Star": "XVII", "The Moon": "XVIII", "The Sun": "XIX",
  "Judgement": "XX", "The World": "XXI",
};

const formatCardName = (name: string, category: string): string => {
  if (category === "major" && MAJOR_ARCANA_NUMERALS[name]) {
    return `${name} (${MAJOR_ARCANA_NUMERALS[name]})`;
  }
  return shortenCardName(name);
};

// Add category IDs here as their minor arcana content is completed
const ENABLED_CATEGORIES = ["major", "wands", "cups", "swords", "pentacles"];

const ALL_CATEGORIES = [
  { id: "major", label: "Major Arcana", keywords: "transitions · influences · big picture", element: "hero's journey", cardImage: "/decks/rider-waite/00-TheFool.png" },
  { id: "wands", label: "Wands", keywords: "inspiration · energy · passion", element: "fire", cardImage: "/decks/rider-waite/Wands01.png" },
  { id: "cups", label: "Cups", keywords: "emotions · feelings · moods", element: "water", cardImage: "/decks/rider-waite/Cups01.png" },
  { id: "swords", label: "Swords", keywords: "intellect · thoughts · words", element: "air", cardImage: "/decks/rider-waite/Swords01.png" },
  { id: "pentacles", label: "Pentacles", keywords: "materials · resources · physicality", element: "earth", cardImage: "/decks/rider-waite/Pentacles01.png" },
];

const CATEGORIES = ALL_CATEGORIES.filter(c => ENABLED_CATEGORIES.includes(c.id));

export const GuidebookIndex = () => {
  const [location, setLocation] = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [allMeanings, setAllMeanings] = useState<Record<string, GuidebookMeaning[]>>({});
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [visibleCategory, setVisibleCategory] = useState<string | null>(null);
  const categoryRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const overlayRef = useRef<HTMLDivElement>(null);

  const hasLoaded = Object.keys(allMeanings).length === CATEGORIES.length;

  // Scroll overlay to top when selecting a card
  useEffect(() => {
    if (selectedCard && overlayRef.current) {
      overlayRef.current.scrollTop = 0;
    }
  }, [selectedCard]);

  // Intersection Observer for mobile scroll-triggered animations
  useEffect(() => {
    if (!isMobileDevice()) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const categoryId = entry.target.getAttribute("data-category-id");
          if (entry.isIntersecting && categoryId) {
            setVisibleCategory(categoryId);
          }
        });
      },
      {
        threshold: 0.6, // Trigger when 60% of the element is visible
        rootMargin: "-10% 0px -30% 0px", // Bias toward upper portion of viewport
      }
    );

    // Observe all category items
    categoryRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [hasLoaded]);

  // Set first category as visible on initial load (mobile) to trigger fan animation
  useEffect(() => {
    if (!isMobileDevice() || !hasLoaded) return;

    // Delay to allow the page to render, then animate the first card stack
    const timer = setTimeout(() => {
      setVisibleCategory(CATEGORIES[0].id);
    }, 200);

    return () => clearTimeout(timer);
  }, [hasLoaded]);

  const setCategoryRef = useCallback((categoryId: string, element: HTMLDivElement | null) => {
    if (element) {
      categoryRefs.current.set(categoryId, element);
    } else {
      categoryRefs.current.delete(categoryId);
    }
  }, []);

  // Handle /guidebook/:slug URL pattern for direct card links
  useEffect(() => {
    const match = location.match(/^\/guidebook\/([^/?]+)/);
    if (match) {
      const slug = match[1];
      setSelectedCard(slug);
      setSelectedCategory(null);
    } else if (location === "/guidebook") {
      // Clear selected card when navigating back to /guidebook
      setSelectedCard(null);
    }
  }, [location]);

  useEffect(() => {
    trackEvent("/guidebook/visited_page");

    const fetchAllCategories = async () => {
      try {
        const results = await Promise.all(
          CATEGORIES.map(cat => getGuidebookByCategory(cat.id))
        );
        const meaningsMap: Record<string, GuidebookMeaning[]> = {};
        CATEGORIES.forEach((cat, index) => {
          meaningsMap[cat.id] = results[index].meanings;
        });
        setAllMeanings(meaningsMap);
      } catch (error) {
        console.error("Failed to load categories:", error);
      }
    };
    fetchAllCategories();
  }, []);

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedCard(null);
  };

  const handleCloseOverlay = () => {
    setSelectedCategory(null);
    setSelectedCard(null);
    // Navigate back to /guidebook if we're on a card URL
    if (location !== "/guidebook") {
      setLocation("/guidebook", { replace: true });
    }
  };

  const handleMeaningClick = (slug: string) => {
    setSelectedCard(slug);
    setSelectedCategory(null);
    // Update URL to /guidebook/slug
    setLocation(`/guidebook/${slug}`, { replace: true });
  };

  const handleBackToCategory = () => {
    // Find which category this card belongs to and go back to it
    for (const [categoryId, meanings] of Object.entries(allMeanings)) {
      if (meanings.some(m => m.slug === selectedCard)) {
        setSelectedCategory(categoryId);
        setSelectedCard(null);
        setLocation("/guidebook", { replace: true });
        return;
      }
    }
    // Fallback: just close
    handleCloseOverlay();
  };

  return (
    <StyledGuidebookContent isIOSNative={isIOSNativeApp()}>
      <PageContent>
        {!hasLoaded && (
          <LoadingContainer>
            <SimpleLoadingIndicator style={{
              width: "30px",
              height: "30px",
              position: "static",
              opacity: 0.5,
            }} />
          </LoadingContainer>
        )}
        <CategoryGrid className={hasLoaded ? "loaded" : ""}>
          {CATEGORIES.map((cat) => {
            // Card fan animation triggers on hover, selection, OR scroll visibility (mobile)
            const shouldFanCards = hoveredCategory === cat.id || selectedCategory === cat.id || visibleCategory === cat.id;
            // Background color only changes on actual hover/selection, not scroll visibility
            const showHoverBackground = hoveredCategory === cat.id || selectedCategory === cat.id;
            const categoryMeanings = allMeanings[cat.id] || [];
            const stackCardImages = categoryMeanings.slice(0, 8).map(m => m.imagePath);

            return (
              <CategoryStackItem
                key={cat.id}
                ref={(el) => setCategoryRef(cat.id, el)}
                data-category-id={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                onMouseEnter={() => setHoveredCategory(cat.id)}
                onMouseLeave={() => setHoveredCategory(null)}
                isHovered={showHoverBackground}
              >
                <GuidebookCardStack
                  images={stackCardImages}
                  fallbackImage={cat.cardImage}
                  isHovered={shouldFanCards}
                  isLoading={stackCardImages.length < 3}
                />
                <div className="category-info">
                  <span className="label">{cat.label}</span>
                  <span className="keywords">{cat.keywords}</span>
                  <span className="element">{cat.element}</span>
                </div>
              </CategoryStackItem>
            );
          })}
        </CategoryGrid>
      </PageContent>

      {/* Overlay for category grid OR card meaning */}
      {(selectedCategory || selectedCard) && (
        <Overlay onClick={handleCloseOverlay} isIOSNative={isIOSNativeApp()}>
          <CategoryOverlay
            ref={overlayRef}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            isIOSNative={isIOSNativeApp()}
          >
            <div className="sticky-header">
              {selectedCard && (
                <img
                  className="back-button"
                  onClick={handleBackToCategory}
                  src="/images/back-nav-icon.svg"
                  alt="Back"
                />
              )}
              <img
                className="x-button"
                onClick={handleCloseOverlay}
                src="/images/exit.svg"
                alt="Close"
              />
            </div>

            {/* Show category grid */}
            {selectedCategory && !selectedCard && (
              <MeaningGrid>
                {(allMeanings[selectedCategory] || []).map((meaning) => (
                  <MeaningName key={meaning.slug} onClick={() => handleMeaningClick(meaning.slug)}>
                    <div className="card-image-container">
                      {meaning.imagePath && (
                        <img src={imageUrl(meaning.imagePath, 120)} alt={meaning.name} className="card-image" />
                      )}
                    </div>
                    {formatCardName(meaning.name, selectedCategory)}
                  </MeaningName>
                ))}
              </MeaningGrid>
            )}

            {/* Show card meaning content */}
            {selectedCard && (
              <GuidebookMeaningContent slug={selectedCard} />
            )}
          </CategoryOverlay>
        </Overlay>
      )}
    </StyledGuidebookContent>
  );
};

const StyledGuidebookContent = styled("div", {
  backgroundColor: "$wash",
  color: "#000",
  "@notmobile": {
    marginLeft: "$navWidth",
  },
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
    color: "$text",
  },
  variants: {
    isIOSNative: {
      true: {
        "@notmobile": {
          marginLeft: 0,
        },
      },
    },
  },
});

const PageContent = styled("div", {
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 40px 120px 40px",
  animation: `${fadeIn} 0.3s ease-out`,
  "@mobile": {
    padding: "10px 36px 280px 36px",
  },
});

const LoadingContainer = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "80px 0",
});

const CategoryGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "20px",
  opacity: 0,
  transition: "opacity 0.4s ease",
  userSelect: "none",
  "&.loaded": {
    opacity: 1,
  },
  "@bp900": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  "@mobile": {
    gridTemplateColumns: "1fr",
    gap: "12px",
  },
});

const CategoryStackItem = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "30px 20px 24px 20px",
  backgroundColor: "#fff",
  borderRadius: "24px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },
  [`.${darkTheme.className} &`]: {
    backgroundColor: "black",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  variants: {
    isHovered: {
      true: {
        backgroundColor: "rgba(0, 0, 0, 0.03)",
        [`.${darkTheme.className} &`]: {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
        },
      },
      false: {},
    },
  },
  "& .category-info": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    marginTop: "80px",
    gap: "4px",
  },
  "& .label": {
    fontFamily: "GorgeousBaby",
    fontSize: "32px",
    fontWeight: "200",
    "@mobile": {
      fontSize: "36px",
    },
  },
  "& .keywords": {
    fontSize: "11px",
    color: "#666",
    fontWeight: "400",
    "@mobile": {
      fontSize: "12px",
    },
    [`.${darkTheme.className} &`]: {
      color: "$textMuted",
    },
  },
  "& .element": {
    fontSize: "11px",
    color: "#464646",
    fontWeight: "300",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    [`.${darkTheme.className} &`]: {
      color: "$textMuted",
    },
  },
});

const Overlay = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(144, 144, 144, 0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
  paddingLeft: "$navWidth",
  "@mobile": {
    paddingLeft: 0,
    top: "48px",
  },
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$scrim",
  },
  variants: {
    isIOSNative: {
      true: {
        "@notmobile": {
          paddingLeft: 0,
        },
      },
    },
  },
});

const CategoryOverlay = styled("div", {
  backgroundColor: "$wash",
  borderRadius: "30px",
  border: "1px solid #000",
  padding: "40px 34px 40px 40px",
  scrollbarGutter: "stable",
  maxWidth: "430px",
  width: "90%",
  maxHeight: vh(75),
  overflowY: "auto",
  position: "relative",
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
    border: "1px solid $border",
  },
  "@mobile": {
    width: "100%",
    height: "100%",
    maxWidth: "none",
    maxHeight: "none",
    borderRadius: 0,
    border: "none",
    borderTop: "1px solid rgba(0, 0, 0, 0.2)",
    padding: "24px 20px 120px 24px",
    [`.${darkTheme.className} &`]: {
      border: "none",
      borderTop: "1px solid rgba(255, 255, 255, 0.2)",
    },
  },
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "6px",
    backgroundColor: "rgba(127, 127, 127, .5)",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
    marginTop: "20px",
    marginBottom: "20px",
  },
  "& .sticky-header": {
    position: "sticky",
    top: "0px",
    zIndex: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    pointerEvents: "none",
    height: 0,
    marginTop: "-24px",
  },
  "& .back-button": {
    position: "relative",
    top: "-10px",
    left: "-18px",
    width: "18px",
    height: "18px",
    cursor: "pointer",
    opacity: "0.8",
    pointerEvents: "auto",
    "&:hover": {
      opacity: "0.5",
    },
    [`.${darkTheme.className} &`]: {
      filter: "invert(1)",
    },
    "@mobile": {
      top: "8px",
      left: "-8px",
    },
  },
  "& .x-button": {
    position: "relative",
    top: "-10px",
    right: "-20px",
    marginLeft: "auto",
    width: "24px",
    height: "24px",
    cursor: "pointer",
    opacity: "0.8",
    pointerEvents: "auto",
    "&:hover": {
      opacity: "0.5",
    },
    [`.${darkTheme.className} &`]: {
      filter: "invert(1)",
    },
    "@mobile": {
      top: "8px",
      right: "-10px",
      // Increase tappable area on mobile with ::before pseudo-element
      "&::before": {
        content: '""',
        position: "absolute",
        top: "-20px",
        right: "-20px",
        bottom: "-20px",
        left: "-20px",
      },
    },
  },
  variants: {
    isIOSNative: {
      true: {
        "@mobile": {
          padding: "24px 20px 180px 24px",
        },
      },
    },
  },
});

const MeaningGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "8px",
  padding: "40px 10px",
  justifyContent: "center",
  margin: "0 auto",
  animation: `${fadeInWithPointerDelay} 0.3s ease-out`,
  userSelect: "none",
  "@mobile": {
    padding: "80px 20px",
  },
});

const MeaningName = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  padding: "12px 8px",
  fontSize: "11px",
  fontWeight: "400",
  lineHeight: "1.2em",
  cursor: "pointer",
  transition: "all 0.15s ease",
  backgroundColor: "#fff",
  borderRadius: "11px",
  border: "1px solid rgba(0, 0, 0, 0.15)",
  [`.${darkTheme.className} &`]: {
    backgroundColor: "black",
    border: "1px solid rgba(255, 255, 255, 0.3)",
  },
  "@notmobile": {
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      [`.${darkTheme.className} &`]: {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      },
    },
  },
  "@mobile": {
    "&:active": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      [`.${darkTheme.className} &`]: {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      },
    },
  },
  "& .card-image-container": {
    width: "45px",
    height: "73px", // Fixed height based on card aspect ratio (120x194 → 45x73)
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .card-image": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "5px",
    [`.${darkTheme.className} &`]: {
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },
  },
});

